import { PrivateImage } from "@/components/PrivateMedia";
import React from "react";
import { Link } from "react-router-dom";
import { Heart, Sparkles, X } from "lucide-react";
import { useI18n } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function photoUrl(u) {
  if (!u) return null;
  return u.startsWith("http") ? u : `${BACKEND_URL}${u}`;
}

function Avatar({ url, name }) {
  const img = photoUrl(url);
  if (img) {
    return (
      <PrivateImage
        src={img}
        alt=""
        className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-4"
        style={{ borderColor: "hsl(var(--nvl-accent))" }}
      />
    );
  }
  return (
    <div
      className="w-32 h-32 md:w-40 md:h-40 rounded-full flex items-center justify-center border-4 font-display text-5xl"
      style={{
        borderColor: "hsl(var(--nvl-accent))",
        backgroundColor: "hsl(var(--muted))",
        color: "hsl(var(--nvl-accent))",
      }}
    >
      {(name || "?").charAt(0).toUpperCase()}
    </div>
  );
}

/**
 * MatchModal — celebratory reveal when a mutual like creates a Match.
 *
 * Props:
 *   open, onClose, match: { match_id, other: {display_name, main_photo_url, ...}, me: {...} }
 *   onKeepDiscovering: () => void
 */
export default function MatchModal({ open, match, onClose, onKeepDiscovering }) {
  const { t } = useI18n();
  if (!open || !match) return null;

  const other = match.other || {};
  const me = match.me || {};

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(10,17,40,0.85)", backdropFilter: "blur(10px)" }}
      onClick={onClose}
      data-testid="match-modal-overlay"
    >
      <div
        className="relative max-w-md w-full rounded-2xl p-8 text-center overflow-hidden"
        style={{
          backgroundColor: "hsl(var(--nvl-bg))",
          border: "1px solid hsl(var(--nvl-accent) / 0.4)",
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="match-modal"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 opacity-60 hover:opacity-100"
          data-testid="match-modal-close"
        >
          <X size={18} />
        </button>

        {/* Sparkle backdrop */}
        <div
          className="absolute inset-0 pointer-events-none opacity-30"
          style={{
            background:
              "radial-gradient(circle at 20% 20%, hsl(var(--nvl-accent) / 0.3), transparent 40%), radial-gradient(circle at 80% 80%, hsl(var(--nvl-accent) / 0.2), transparent 40%)",
          }}
        />

        <div className="relative">
          <Sparkles
            size={28}
            className="mx-auto mb-2"
            style={{ color: "hsl(var(--nvl-accent))" }}
          />
          <h2 className="font-display text-3xl md:text-4xl" data-testid="match-modal-title">
            {t("app.match.itsAMatch")}
          </h2>
          <p className="mt-2 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.match.subtitle")}
          </p>

          <div className="mt-6 flex items-center justify-center gap-4">
            <Avatar url={me.main_photo_url} name={me.display_name} />
            <Heart
              size={32}
              style={{ color: "hsl(var(--nvl-accent))" }}
              className="animate-pulse"
            />
            <Avatar url={other.main_photo_url} name={other.display_name} />
          </div>

          <p className="mt-5 font-display text-xl" data-testid="match-modal-name">
            {other.display_name}
          </p>

          <div className="mt-8 flex flex-col gap-2">
            <Link
              to={`/app/messages/${match.match_id}`}
              className="nvl-btn nvl-btn-primary w-full"
              onClick={onClose}
              data-testid="match-modal-send-message"
            >
              {t("app.match.sendMessage")}
            </Link>
            <button
              onClick={() => {
                onClose();
                if (onKeepDiscovering) onKeepDiscovering();
              }}
              className="nvl-btn nvl-btn-outline w-full"
              data-testid="match-modal-keep-discovering"
            >
              {t("app.match.keepDiscovering")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
